<script setup lang="ts">
import StfInput from '@/ui/input/StfInput.vue';
import CountriesSelect from '@/components/contact-view/countries-select.vue';
import AddressSelect from '@/components/contact-view/address-select.vue';
import { useOwnerStore } from '@/store/owner';
import { storeToRefs } from 'pinia';
import StfDivider from '@/ui/divider/StfDivider.vue';
import StfRow from '@/ui/columns/StfRow.vue';
import StfCol from '@/ui/columns/StfCol.vue';
import StfBody from '@/ui/typography/StfBody.vue';
import { MarkOwnerType } from '@/core/interface/api-interface';
import {useApplicationStore} from '@/store/application';
import BusinessForm from '@/components/contact-view/business-form.vue';
import { CountryCode } from '@/config/country/type';
import useCountry from '@/composables/countryDependable';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import useMobile from '@/composables/useMobileDependable';

const { t } = useI18n();
const { hasValidation } = storeToRefs(useApplicationStore());
const ownerStore = useOwnerStore();
const applicationCountry = useCountry();
const {isMobile} = useMobile();
const { addOwnerName, removeOwnerName } = ownerStore;
const { 
    ownerNames,
    ownerType,
    country,
    address,
    suburb,
    stateRegion,
    postCode,
    email,
    mobileNumber,
    ownerNamesError,
    ownerNamesIsValid,
    suburbError,
    stateRegionError,
    postCodeError,
    emailError,
    mobileNumberError,
    suburbIsValid,
    stateRegionIsValid,
    postCodeIsValid,
    emailIsValid,
    mobileNumberIsValid,
} = storeToRefs(ownerStore);

// Initialize with one empty owner name field if there are none
if (ownerNames.value.length === 0) {
    ownerNames.value.push('');
}

const updateOwnerName = (index: number, value: string) => {
    ownerNames.value[index] = value;
};

const addJointOwner = () => {
    addOwnerName('');
};

const removeOwner = (index: number) => {
    if (ownerNames.value.length > 1) {
        removeOwnerName(index);
    }
};

const ownerNameLabel = computed(() => {
    return ownerType.value === MarkOwnerType.PERSON ? 'Individual\'s name' : 'Company name';
});

const ownerNamePlaceholder = computed(() => {
    if (ownerType.value === MarkOwnerType.PERSON) {
        return 'Enter the full name of the Individual';
    }

    if (isMobile.value) {
        return t('message.contact.ownerNamePlaceholderForCompanyMobile');
    }

    return t('message.contact.ownerNamePlaceholderForCompany');
});

const haveBusinessNumber = computed(() => {
    return applicationCountry.code === CountryCode.AU || ownerType.value === MarkOwnerType.BUSINESS && applicationCountry.code !== CountryCode.NZ 
});
</script>

<template>
    <StfDivider></StfDivider>
    <template v-for="(ownerName, index) in ownerNames" :key="index">
        <StfInput 
            :modelValue="ownerName" 
            @update:modelValue="(value) => updateOwnerName(index, value)" 
            :label="ownerNameLabel" 
            :placeholder="ownerNamePlaceholder" 
            required
            class="mb-2"
            :error="hasValidation ? ownerNamesError : null"
            :success="index === 0 && ownerNamesIsValid"
        >
            <template v-if="index > 0" #label:happen>
                <StfBody class="stf-clickable" @click="removeOwner(index)"><u>Remove owner</u></StfBody>
            </template>
        </StfInput>
    </template>
    <StfBody class="stf-clickable" @click="addJointOwner"><u>Add Joint Owner</u></StfBody>
    <business-form v-if="haveBusinessNumber" />
    <StfDivider></StfDivider>
    <StfRow>
        <StfCol col="12" colDesktop="auto"><countries-select v-model="country"></countries-select></StfCol>
        <StfCol col="12" colDesktop="auto"><address-select></address-select></StfCol>
    </StfRow>
    <StfRow v-if="address.length > 0">
        <StfCol col="12" colDesktop="auto"><StfInput v-model="suburb" label="Suburb" placeholder="Suburb" required :error="hasValidation ? suburbError : null" :success="suburbIsValid"></StfInput></StfCol>
        <StfCol col="12" colDesktop="auto"><StfInput v-model="stateRegion" :label="t('message.contact.stateRegionLabel')" :placeholder="t('message.contact.stateRegionLabel')" required :error="hasValidation ? stateRegionError : null" :success="stateRegionIsValid"></StfInput></StfCol>
        <StfCol col="12" colDesktop="auto"><StfInput v-model="postCode" label="Post Code" placeholder="Post Code" required :error="hasValidation ? postCodeError : null" :success="postCodeIsValid"></StfInput></StfCol>
    </StfRow>
    <StfDivider></StfDivider>
    <StfRow>
        <StfCol col="12" colDesktop="auto"><StfInput v-model="email" label="Email" placeholder="Email" required :error="hasValidation ? emailError : null" :success="emailIsValid"></StfInput></StfCol>
        <StfCol col="12" colDesktop="auto"><StfInput v-model="mobileNumber" label="Mobile Number" placeholder="Mobile Number" required :error="hasValidation ? mobileNumberError : null" :success="mobileNumberIsValid"></StfInput></StfCol>
    </StfRow>
</template>
